import { store } from "../store/store";

/** Bill Audit tabs opened from HMO via window.open — notified on logout. */
const billAuditChildWindows = new Map<Window, string>();

function parseStoredToken(raw: string | null): string {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === "string" ? parsed : trimmed;
  } catch {
    return trimmed;
  }
}

export function getStoredAccessToken(): string {
  const fromRedux = store.getState().auth.accessToken;
  if (fromRedux) {
    return parseStoredToken(String(fromRedux));
  }

  const edgeProToken = localStorage.getItem("edgeProAccessToken");
  if (edgeProToken) {
    return parseStoredToken(edgeProToken);
  }

  const accessToken = localStorage.getItem("accessToken");
  if (accessToken) {
    return parseStoredToken(accessToken);
  }

  return "";
}

function registerBillAuditChildWindow(child: Window, childOrigin: string): void {
  if (!child || child.closed) return;
  billAuditChildWindows.set(child, childOrigin);

  const pruneTimer = window.setInterval(() => {
    if (child.closed) {
      billAuditChildWindows.delete(child);
      window.clearInterval(pruneTimer);
    }
  }, 2000);
}

function pruneClosedBillAuditChildren(): void {
  billAuditChildWindows.forEach((_, child) => {
    if (child.closed) {
      billAuditChildWindows.delete(child);
    }
  });
}

function postToBillAuditChild(
  child: Window,
  childOrigin: string,
  message: Record<string, unknown>,
): void {
  try {
    if (!child.closed) {
      child.postMessage(message, childOrigin);
    }
  } catch {
    try {
      if (!child.closed) {
        child.postMessage(message, "*");
      }
    } catch {
      // no-op
    }
  }
}

function ensureHmoBillAuditAuthParams(url: string): string {
  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has("parent_source")) {
      parsed.searchParams.set("parent_source", "hmo");
    }
    if (!parsed.searchParams.has("platform")) {
      parsed.searchParams.set("platform", "HMO");
    }
    if (!parsed.searchParams.has("parent_origin") && window.location?.origin) {
      parsed.searchParams.set("parent_origin", window.location.origin);
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

/** Push session token to a Bill Audit tab opened from HMO (works even when window.opener is null). */
export function pushTokenToBillAuditChild(
  child: Window,
  childOrigin: string,
): void {
  const respond = () => ({
    type: "TOKEN_RESPONSE" as const,
    token: getStoredAccessToken(),
    source: "hmo" as const,
  });

  let attempts = 0;
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const pollMs = isLocal ? 150 : 250;
  const maxAttempts = isLocal ? 40 : 30;

  const send = () => {
    attempts += 1;
    postToBillAuditChild(child, childOrigin, respond());

    if (getStoredAccessToken() && attempts >= 2) {
      window.clearInterval(timer);
      return;
    }
    if (attempts >= maxAttempts || child.closed) {
      window.clearInterval(timer);
    }
  };

  send();
  const timer = window.setInterval(send, pollMs);
}

/**
 * Open Bill Audit and proactively send the HMO token to the child window.
 * Child-initiated postMessage via window.opener is unreliable across origins.
 */
export function openBillAuditWindow(url: string | null | undefined): void {
  if (!url) return;

  const finalUrl = ensureHmoBillAuditAuthParams(url);
  let openUrl = finalUrl;
  const token = getStoredAccessToken();

  try {
    const parsed = new URL(finalUrl);
    if (token) {
      const hash = new URLSearchParams();
      hash.set("token", token);
      parsed.hash = hash.toString();
      openUrl = parsed.toString();
    }
  } catch {
    return;
  }

  let childOrigin = "*";
  try {
    childOrigin = new URL(openUrl).origin;
  } catch {
    return;
  }

  const child = window.open(openUrl, "_blank");
  if (!child) return;

  registerBillAuditChildWindow(child, childOrigin);

  if (token) {
    pushTokenToBillAuditChild(child, childOrigin);
  }
}

/** Bill Audit runs on *.gsevenpro.com and requests a token from the HMO parent tab. */
export function isBillAuditOrigin(origin: string): boolean {
  const normalized = String(origin || "").toLowerCase();
  if (!normalized) return false;
  return (
    normalized.includes("billaudit") ||
    normalized.includes("localhost") ||
    normalized.includes("127.0.0.1")
  );
}

export function isAllowedBillAuditReturnUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (host.endsWith("gsevenpro.com") && host.includes("billaudit")) {
      return parsed.protocol === "https:";
    }
    if (host === "localhost" || host === "127.0.0.1") {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Notify Bill Audit tabs/iframes opened from HMO that the session ended. */
export function broadcastBillAuditLogout(): void {
  if (typeof window === "undefined") return;

  const message = { type: "SESSION_LOGOUT", source: "hmo" as const };

  pruneClosedBillAuditChildren();
  billAuditChildWindows.forEach((childOrigin, child) => {
    postToBillAuditChild(child, childOrigin, message);
  });
  billAuditChildWindows.clear();

  document.querySelectorAll("iframe").forEach((iframe) => {
    try {
      iframe.contentWindow?.postMessage(message, "*");
    } catch {
      // no-op
    }
  });

  try {
    window.postMessage(message, "*");
  } catch {
    // no-op
  }
}

export function setupBillAuditTokenBridge(): () => void {
  const handleMessage = (event: MessageEvent) => {
    if (!isBillAuditOrigin(event.origin)) return;
    if (event.data?.type === "SESSION_LOGOUT") return;
    if (event.data !== "getToken") return;

    const source = event.source as Window | null;
    if (!source) return;

    registerBillAuditChildWindow(source, event.origin);

    const respond = (token: string) => {
      source.postMessage(
        { type: "TOKEN_RESPONSE", token: token || "", source: "hmo" },
        event.origin,
      );
    };

    const token = getStoredAccessToken();
    respond(token);
    if (token) return;

    let attempts = 0;
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    const pollMs = isLocal ? 150 : 250;
    const maxAttempts = isLocal ? 20 : 24;

    const retryTimer = window.setInterval(() => {
      attempts += 1;
      const nextToken = getStoredAccessToken();
      respond(nextToken);
      if (nextToken || attempts >= maxAttempts) {
        window.clearInterval(retryTimer);
      }
    }, pollMs);
  };

  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
}
